/**
 * Recuperación de chunks lazy (LAZY-CHUNK-RECOVERY-P0).
 *
 * Problema: cada deploy cambia el hash de los chunks de las rutas lazy. Una
 * pestaña abierta antes del deploy conserva el bundle viejo y, al navegar a
 * /admin, /panel, etc., pide un chunk que ya no existe. Vercel responde con el
 * index.html (200 text/html), el import() falla y, sin recuperación, React
 * desmonta la raíz: pantalla en blanco. El watchdog de index.html no ayuda
 * porque solo vigila el arranque.
 *
 * Solución: ante `vite:preloadError`, UNA recarga completa por episodio (trae el
 * index.html nuevo con los hashes vigentes). Misma filosofía anti-bucle que el
 * watchdog de arranque:
 *   - la marca vive en sessionStorage (por pestaña); sin sessionStorage no se
 *     recarga nunca;
 *   - si la marca ya está puesta y vigente, el error se deja seguir y lo muestra
 *     el ErrorBoundary con «Reintentar» (manual, que no limpia la marca);
 *   - la marca se limpia solo con evidencia positiva: un chunk lazy que carga
 *     bien. Montar la app NO la limpia (la ruta que falló puede volver a fallar
 *     justo después del montaje y eso sería un bucle);
 *   - una marca de más de EPISODE_TTL_MS se considera de otro episodio.
 *
 * No toca Auth, sesión, Supabase, router ni caché.
 */

export const CHUNK_RELOAD_KEY = 'lc_chunk_reload';
export const EPISODE_TTL_MS = 10 * 60 * 1000;

let reloadPending = false;

/** `undefined` = sessionStorage no disponible; `null` = disponible y sin marca. */
function readMark(): string | null | undefined {
  try {
    return window.sessionStorage.getItem(CHUNK_RELOAD_KEY);
  } catch {
    return undefined;
  }
}

function writeMark(now: number): boolean {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

/** True mientras hay una recarga de recuperación en curso. */
export function isChunkReloadPending(): boolean {
  return reloadPending;
}

/**
 * Decide qué hacer ante un `vite:preloadError`. Devuelve 'reload' si recarga
 * (y cancela el evento) o 'propagate' si deja que el error llegue al
 * ErrorBoundary.
 */
export function handleChunkLoadError(event: Event, now: number = Date.now()): 'reload' | 'propagate' {
  // Ya se está recargando: se tragan los errores de otros chunks del mismo episodio.
  if (reloadPending) {
    event.preventDefault();
    return 'reload';
  }

  const mark = readMark();
  if (mark === undefined) return 'propagate';

  const at = mark === null ? NaN : Number(mark);
  // Marca vigente (incluida una con fecha futura): la recarga de este episodio
  // ya se usó. Una marca ilegible se trata como ausente: se sobrescribe con una
  // válida, así que no puede encadenar recargas.
  if (Number.isFinite(at) && now - at < EPISODE_TTL_MS) return 'propagate';

  if (!writeMark(now)) return 'propagate';
  event.preventDefault();
  reloadPending = true;
  window.location.reload();
  return 'reload';
}

/** Un chunk lazy cargó bien: el episodio terminó y se libera la recarga. */
export function markChunkLoadSucceeded(): void {
  try {
    if (window.sessionStorage.getItem(CHUNK_RELOAD_KEY) !== null) {
      window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    }
  } catch {
    /* sin sessionStorage no hay marca que limpiar */
  }
}

let installed = false;

/** Registra el listener global. Debe llamarse antes del primer import() lazy. */
export function installChunkRecovery(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('vite:preloadError', (event) => {
    handleChunkLoadError(event);
  });
}
